import React, { useEffect, useRef, useState } from "react";
import { Avatar, Box, Chip, Link, Paper, Stack, Tabs, Tab, Typography } from "@mui/material";
import Grid from '@mui/material/Grid2';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import TitleDescription from "../components/TitleDescription";
import Title from "../components/Title";
import TitleDescriptionImage from "../components/TitleDescriptionImage";
import { ReactComponent as ComprehensiveAnalyticsReportingComponent } from "../assets/comprehensive-analytics-reporting.svg"
import { ReactComponent as SeamlessIntegrationWithCBSComponent } from "../assets/seamless-integration-with-cbs.svg"
import { ReactComponent as SeamlessSupport247Component } from "../assets/247-support.svg"
import riskGuageSequenceDiagram from "../assets/riskgauge-flow.png"
import { useLocation } from "react-router-dom";
import { fetchAndFilterOpenAPISpec } from "../utils/openapi-filter";
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { ENCRYPTION_CODE_SAMPLES } from './encryption-samples';

function navigateToSection(hash: string) {
    const element = document.getElementById(hash);
    if (element) {
        var headerOffset = 45;
        var elementPosition = element.getBoundingClientRect().top;
        var offsetPosition = elementPosition + window.scrollY - headerOffset;
        window.scrollTo({
            top: offsetPosition,
            behavior: "smooth"
        });
    }
}

const imageStyle = {
    border: '2px solid #ccc',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
    borderRadius: '8px',
    padding: '10px',
    maxWidth: '75%',
    marginBottom: '30px'
};

const PRIMARY_COLOR = '#6C4FE0';
const BORDER_COLOR = '#E2E8F0';
const FLOW_MAX_WIDTH = 900;

const codeBlockStyle = {
    backgroundColor: '#1E293B',
    color: '#E2E8F0',
    borderRadius: '6px',
    padding: '14px 18px',
    fontFamily: '"SF Mono","Monaco","Inconsolata","Roboto Mono",monospace',
    fontSize: '12.5px',
    lineHeight: 1.6,
    whiteSpace: 'pre' as const,
    overflowX: 'auto' as const,
    marginTop: '8px',
    marginBottom: '8px',
};

const consentNotificationPayload = `{
  "consentHandle": "6c1c3748-9fff-4999-84a3-b824064003f9",
  "status": "ACTIVE",
  "consentId": "bbe0e807-0b1b-4e37-8e19-204d5424d0b2",
  "requestId": "07e4f36f-b159-40b2-9b2b-66c1167ccb7d",
  "sessionId": "d152a689-4ea6-4de8-b2b3-6bd178a1b59f"
}`;

const dataNotificationPayload = `{
  "consentHandle": "6c1c3748-9fff-4999-84a3-b824064003f9",
  "status": "READY",
  "dataSessionId": "d152a689-4ea6-4de8-b2b3-6bd178a1b59f",
  "linkRefNumber": "d9d755ce-4c79-407c-8d01-156e9091942a",
  "dataDeliveryStatus": "READY",
  "notificationType": "FI_DATA",
  "desc": "ACTIVE",
  "errStg": null,
  "errMsg": "",
  "fip": "BARB0KIMXXX",
  "attempt": null
}`;

type ChipKind = 'success' | 'warning' | 'error' | 'info' | 'neutral';

const CHIP_STYLES: Record<ChipKind, { bg: string; color: string; border: string }> = {
    success: { bg: '#E6F4EA', color: '#137333', border: '#CEEAD6' },
    warning: { bg: '#FEF7E0', color: '#B06000', border: '#FEEFC3' },
    error: { bg: '#FCE8E6', color: '#C5221F', border: '#FAD2CF' },
    info: { bg: '#E8F0FE', color: '#1967D2', border: '#D2E3FC' },
    neutral: { bg: '#F1F3F4', color: '#3C4043', border: '#E0E3E6' },
};

interface StatusRowProps {
    label: string;
    kind: ChipKind;
    description: React.ReactNode;
}

const StatusRow: React.FC<StatusRowProps> = ({ label, kind, description }) => {
    const styles = CHIP_STYLES[kind];
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', py: 0.5 }}>
            <Box sx={{ width: 220, flexShrink: 0, mr: 2 }}>
                <Chip
                    size="small"
                    label={label}
                    sx={{
                        backgroundColor: styles.bg,
                        color: styles.color,
                        border: `1px solid ${styles.border}`,
                        fontWeight: 600,
                        fontSize: 11.5,
                        fontFamily: '"SF Mono","Monaco","Inconsolata","Roboto Mono",monospace',
                        height: 22,
                    }}
                />
            </Box>
            <Typography fontSize={13} sx={{ color: '#4A5568', lineHeight: 1.55 }}>
                {description}
            </Typography>
        </Box>
    );
};

interface StatusLegendProps {
    field: string;
    items: StatusRowProps[];
}

const StatusLegend: React.FC<StatusLegendProps> = ({ field, items }) => (
    <Box sx={{ mt: 1.5, mb: 0.5 }}>
        <Typography fontSize={13} sx={{ color: '#2D3748', mb: 0.5 }}>
            Possible values for <Box component="code" sx={inlineCodeStyle}>{field}</Box>:
        </Typography>
        <Box sx={{ pl: 0.5 }}>
            {items.map(item => <StatusRow key={item.label} {...item} />)}
        </Box>
    </Box>
);

const inlineCodeStyle = {
    backgroundColor: '#F1F3F4',
    color: '#1A202C',
    borderRadius: '4px',
    padding: '1px 6px',
    fontFamily: '"SF Mono","Monaco","Inconsolata","Roboto Mono",monospace',
    fontSize: '12.5px',
};

interface EndpointBadgeProps {
    method: 'POST' | 'GET' | 'PUT' | 'DELETE';
    path: string;
}

const METHOD_COLORS: Record<EndpointBadgeProps['method'], string> = {
    POST: '#1967D2',
    GET: '#137333',
    PUT: '#B06000',
    DELETE: '#C5221F',
};

const EndpointBadge: React.FC<EndpointBadgeProps> = ({ method, path }) => (
    <Box
        component="span"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: '4px',
            border: `1px solid ${BORDER_COLOR}`,
            backgroundColor: '#FFFFFF',
            fontFamily: '"SF Mono","Monaco","Inconsolata","Roboto Mono",monospace',
            fontSize: '12.5px',
            overflow: 'hidden',
            verticalAlign: 'middle',
            mx: 0.25,
        }}
    >
        <Box
            component="span"
            sx={{
                backgroundColor: METHOD_COLORS[method],
                color: '#FFFFFF',
                fontWeight: 700,
                px: 0.75,
                py: '2px',
                letterSpacing: 0.3,
            }}
        >
            {method}
        </Box>
        <Box component="span" sx={{ px: 1, py: '2px', color: '#1A202C' }}>
            {path}
        </Box>
    </Box>
);

const GlanceApiRow: React.FC<{ number: number; label: string }> = ({ number, label }) => (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Avatar
            sx={{
                width: 22,
                height: 22,
                fontSize: 12,
                fontWeight: 700,
                bgcolor: PRIMARY_COLOR,
                mr: 1.25,
            }}
        >
            {number}
        </Avatar>
        <Typography fontSize={13.5} sx={{ color: '#1A202C' }}>{label}</Typography>
    </Box>
);

const GlanceWebhookRow: React.FC<{ label: string; required: boolean }> = ({ label, required }) => {
    const styles = required ? CHIP_STYLES.success : CHIP_STYLES.neutral;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <NotificationsNoneOutlinedIcon sx={{ color: PRIMARY_COLOR, fontSize: 18, mr: 1 }} />
            <Typography fontSize={13.5} sx={{ color: '#1A202C', mr: 1 }}>{label}</Typography>
            <Chip
                size="small"
                label={required ? 'Required' : 'Optional'}
                sx={{
                    height: 18,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    backgroundColor: styles.bg,
                    color: styles.color,
                    border: `1px solid ${styles.border}`,
                }}
            />
        </Box>
    );
};

interface StepCardProps {
    stepNumber: number;
    icon: React.ReactNode;
    title: string;
    isLast?: boolean;
    children: React.ReactNode;
}

const StepCard: React.FC<StepCardProps> = ({ stepNumber, icon, title, isLast, children }) => (
    <Box sx={{ display: 'flex', position: 'relative' }}>
        <Box
            sx={{
                flexShrink: 0,
                mr: 2.5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            <Avatar
                sx={{
                    bgcolor: PRIMARY_COLOR,
                    width: 36,
                    height: 36,
                    fontSize: 15,
                    fontWeight: 'bold',
                    zIndex: 1,
                    boxShadow: '0 2px 4px rgba(108, 79, 224, 0.25)',
                }}
            >
                {stepNumber}
            </Avatar>
            {!isLast && (
                <Box sx={{ flex: 1, width: 2, backgroundColor: BORDER_COLOR, mt: 0.5 }} />
            )}
        </Box>
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                mb: isLast ? 0 : 3,
                border: `1px solid ${BORDER_COLOR}`,
                borderLeft: `4px solid ${PRIMARY_COLOR}`,
                borderRadius: 2,
                flex: 1,
                minWidth: 0,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ color: PRIMARY_COLOR, display: 'inline-flex', mr: 1 }}>
                    {icon}
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#1A202C' }}>
                    {title}
                </Typography>
            </Box>
            {children}
        </Paper>
    </Box>
);

const CodeBlock: React.FC<{ title?: string; fullWidth?: boolean; language?: string; children: string }> = ({ title, fullWidth, language, children }) => (
    <Box sx={{ mb: 2, ...(!fullWidth && { maxWidth: '75%' }) }}>
        {title && (
            <Typography variant="caption" sx={{
                display: 'block',
                bgcolor: '#2D3748',
                color: '#E2E8F0',
                px: 2,
                py: 0.5,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                fontFamily: '"SF Mono","Monaco","Inconsolata","Roboto Mono",monospace',
                fontSize: 12,
            }}>
                {title}
            </Typography>
        )}
        <SyntaxHighlighter
            language={language || 'text'}
            style={oneDark}
            customStyle={{
                margin: 0,
                borderRadius: title ? '0 0 8px 8px' : '8px',
                fontSize: 13,
                lineHeight: 1.6,
            }}
        >
            {children}
        </SyntaxHighlighter>
    </Box>
);

const SubTitle: React.FC<{ children: string }> = ({ children }) => (
    <Typography sx={{ fontSize: 16, fontWeight: 600, pt: 2, pb: 1 }}>
        {children}
    </Typography>
);

const Paragraph: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography sx={{ fontSize: 14, pb: 1, lineHeight: 1.7, maxWidth: '75%' }}>
        {children}
    </Typography>
);

const LanguageTabs: React.FC = () => {
    const [tab, setTab] = useState(0);
    const sample = ENCRYPTION_CODE_SAMPLES[tab];

    return (
        <Box sx={{ maxWidth: '90%', pb: 1 }}>
            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{
                    minHeight: 36,
                    '& .MuiTab-root': {
                        minHeight: 36,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: 13,
                    },
                    '& .MuiTabs-indicator': {
                        backgroundColor: '#6C4FE0',
                    },
                }}
            >
                {ENCRYPTION_CODE_SAMPLES.map((s) => (
                    <Tab key={s.label} icon={s.icon} iconPosition="start" label={s.label} />
                ))}
            </Tabs>
            <Grid container spacing={2} sx={{ pt: 2 }}>
                <Grid size={6}>
                    <CodeBlock title={`${sample.label} — ${sample.encrypt.title}`} language={sample.language} fullWidth>
                        {sample.encrypt.code}
                    </CodeBlock>
                </Grid>
                <Grid size={6}>
                    <CodeBlock title={`${sample.label} — ${sample.decrypt.title}`} language={sample.language} fullWidth>
                        {sample.decrypt.code}
                    </CodeBlock>
                </Grid>
            </Grid>
        </Box>
    );
};

const RiskGaugePage: React.FC = React.memo(() => {
    const rapidocRef = useRef<any>(null);
    const location = useLocation();
    useEffect(() => {
        if (location.hash.length === 0) {
            window.scrollTo({
                top: 0,
                behavior: "smooth"
            })
            return;
        }

        const hash = location.hash.slice(1);
        navigateToSection(hash);
    }, [location.hash]);


    useEffect(() => {
        const loadFilteredSpec = async () => {
            if (rapidocRef.current) {
                try {
                    const filteredSpec = await fetchAndFilterOpenAPISpec({
                        specUrl: 'https://dhanaprayoga.fiu.finfactor.in/risk-gauge-internal/v1/openapi',
                        includePaths: [
                            '/risk-gauge/api/v1/login',
                            '/risk-gauge/api/v1/consent*',
                            '/risk-gauge/api/v1/data/sessions/*/status',
                            '/risk-gauge/api/v1/data*',
                            '/risk-gauge/api/v1/fi-data*',
                        ]
                    });

                    rapidocRef.current.loadSpec(filteredSpec);
                } catch (error) {
                    console.error('Failed to load API spec:', error);
                }
            }
        };

        loadFilteredSpec();
    }, []);

    return <>
        <Box sx={{
            paddingLeft: 4
        }}>
            <TitleDescription title="Risk Gauge" description="Your Bank Statement Analyser for accurate and low-cost credit decisioning." />
            <>
                <Title title="Why Risk Gauge?" />
                <Grid container spacing={3} paddingTop={1}>
                    <Grid size={4}>
                        <TitleDescriptionImage
                            title="Intelligent Underwriting Insights"
                            description="Accurately identify income, obligations, frauds along with UPI transaction categorisations to assess customer's financial health."
                            svg={ComprehensiveAnalyticsReportingComponent}
                            maxWidth={290}
                        />
                    </Grid>
                    <Grid size={4}>
                        <TitleDescriptionImage
                            title="Multiple data sources"
                            description="Supports multiple data fetch flows like AA and PDF statement upload."
                            svg={SeamlessIntegrationWithCBSComponent}
                            maxWidth={290}
                        />
                    </Grid>
                    <Grid size={4}>
                        <TitleDescriptionImage
                            title="Customise as per your needs"
                            description="Ability to add additional insights to assist your decision making"
                            svg={SeamlessSupport247Component}
                            maxWidth={290}
                        />
                    </Grid>
                </Grid>
            </>
        </Box>
        <Box sx={{
            paddingLeft: 4
        }}>
            <>
                <Title title="How Risk Gauge Works - Sequence Diagram" paddingBottom={4} paddingTop={6} />
                <img src={riskGuageSequenceDiagram} alt="Risk Gauge sequence diagram showing the flow" style={imageStyle} />
            </>
        </Box>
        <span id="api-integration" />
        <Box sx={{
            paddingLeft: 4,
            paddingRight: 4,
        }}>
            <Title title="API Integration Flow" paddingTop={2} paddingBottom={1} />
            <Typography fontSize={14} gutterBottom sx={{ maxWidth: FLOW_MAX_WIDTH }}>
                The steps below walk through the end-to-end sequence of APIs to call and webhook notifications to handle when integrating with Risk Gauge.
            </Typography>

            <Paper
                elevation={0}
                sx={{
                    mt: 2,
                    mb: 3,
                    p: 2.5,
                    background: 'linear-gradient(135deg, #F8F6FE 0%, #F1ECFB 100%)',
                    border: `1px solid #E9E4FB`,
                    borderRadius: 2,
                    maxWidth: FLOW_MAX_WIDTH,
                }}
            >
                <Typography fontSize={14} fontWeight={700} sx={{ color: '#1A202C', mb: 1.75 }}>
                    Integration at a glance — just{' '}
                    <Box component="span" sx={{ color: PRIMARY_COLOR }}>3 APIs</Box>
                    {' '}and{' '}
                    <Box component="span" sx={{ color: PRIMARY_COLOR }}>2 webhooks</Box>
                </Typography>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography
                            fontSize={11}
                            fontWeight={700}
                            sx={{ color: '#4A5568', textTransform: 'uppercase', letterSpacing: 0.6, mb: 1 }}
                        >
                            3 APIs to call
                        </Typography>
                        <Stack spacing={0.75}>
                            <GlanceApiRow number={1} label="Login" />
                            <GlanceApiRow number={2} label="Consent Request" />
                            <GlanceApiRow number={3} label="Get Data" />
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography
                            fontSize={11}
                            fontWeight={700}
                            sx={{ color: '#4A5568', textTransform: 'uppercase', letterSpacing: 0.6, mb: 1 }}
                        >
                            2 webhooks to handle
                        </Typography>
                        <Stack spacing={0.75}>
                            <GlanceWebhookRow label="Consent notification" required={false} />
                            <GlanceWebhookRow label="Data notification" required={true} />
                        </Stack>
                    </Grid>
                </Grid>
                <Typography fontSize={12.5} sx={{ color: '#4A5568', mt: 1.75, lineHeight: 1.55 }}>
                    The consent webhook is optional — you can alternatively poll{' '}
                    <Box component="code" sx={inlineCodeStyle}>/consent-status</Box>
                    {' '}— but the data webhook is required to know when the analysed report is ready to be fetched.
                </Typography>
            </Paper>

            <Box sx={{ mt: 3, maxWidth: FLOW_MAX_WIDTH }}>
                <StepCard
                    stepNumber={1}
                    icon={<LockOutlinedIcon fontSize="small" />}
                    title="Authenticate using the Login API"
                >
                    <Typography fontSize={14} sx={{ mb: 1 }}>
                        Start by calling <EndpointBadge method="POST" path="/risk-gauge/api/v1/login" /> to obtain a service-to-service access token.
                    </Typography>
                    <Typography fontSize={14} sx={{ mb: 1 }}>
                        The token is valid for <strong>24 hours</strong> and must be supplied in the <Box component="code" sx={inlineCodeStyle}>Authorization</Box> header of every subsequent Risk Gauge API call.
                    </Typography>
                    <Box
                        sx={{
                            mt: 1.5,
                            p: 1.25,
                            backgroundColor: '#F5F3FF',
                            border: `1px solid #E9E4FB`,
                            borderRadius: 1.5,
                            fontSize: 13,
                            color: '#4A3FA8',
                        }}
                    >
                        <strong>Tip:</strong> cache the token on your side and refresh it close to expiry — you don't need to fetch a new one for every request.
                    </Box>
                </StepCard>

                <StepCard
                    stepNumber={2}
                    icon={<LinkOutlinedIcon fontSize="small" />}
                    title="Generate a Consent Request"
                >
                    <Typography fontSize={14} sx={{ mb: 1 }}>
                        Call <EndpointBadge method="POST" path="/risk-gauge/api/v1/consent-request" /> with the customer's identifier and the report type you want to fetch.
                    </Typography>
                    <Typography fontSize={14}>
                        The response contains a consent URL. Redirect the customer to this URL so they can complete the Account Aggregator consent journey.
                    </Typography>
                </StepCard>

                <StepCard
                    stepNumber={3}
                    icon={<NotificationsNoneOutlinedIcon fontSize="small" />}
                    title="Receive the Consent Notification"
                >
                    <Typography fontSize={14} sx={{ mb: 1 }}>
                        After the customer completes (or declines) the consent journey, Risk Gauge POSTs a consent notification to your configured webhook:
                    </Typography>
                    <Box component="pre" sx={codeBlockStyle}>{consentNotificationPayload}</Box>
                    <StatusLegend
                        field="status"
                        items={[
                            { label: 'ACTIVE', kind: 'success', description: 'Consent approved by the customer and ready to be used. Data fetch is automatically triggered.' },
                            { label: 'REQUESTED', kind: 'info', description: 'Initial state — the consent request has been raised but the customer has not acted on it yet.' },
                            { label: 'PENDING', kind: 'warning', description: 'Consent is awaiting the customer\u2019s decision at the AA.' },
                            { label: 'PARTIAL_FIP_ACK', kind: 'warning', description: 'Only some FIPs have acknowledged the consent; others are still pending.' },
                            { label: 'PAUSED', kind: 'warning', description: 'Consent is temporarily paused and can resume later.' },
                            { label: 'REJECTED', kind: 'error', description: 'The customer declined the consent on the AA.' },
                            { label: 'REVOKED', kind: 'error', description: 'A previously active consent was revoked by the customer.' },
                            { label: 'EXPIRED', kind: 'error', description: 'The consent validity period has elapsed.' },
                            { label: 'FAILED', kind: 'error', description: 'Consent processing failed at the AA or FIP.' },
                        ]}
                    />
                </StepCard>

                <StepCard
                    stepNumber={4}
                    icon={<CloudDoneOutlinedIcon fontSize="small" />}
                    title="Receive the Data Notification"
                >
                    <Typography fontSize={14} sx={{ mb: 1 }}>
                        Once consent is <Box component="code" sx={inlineCodeStyle}>ACTIVE</Box>, Risk Gauge automatically triggers the data fetch. When the data is ready, a second notification is delivered to the same webhook:
                    </Typography>
                    <Box component="pre" sx={codeBlockStyle}>{dataNotificationPayload}</Box>

                    <StatusLegend
                        field="status"
                        items={[
                            { label: 'READY', kind: 'success', description: 'Data fetch completed successfully for the session.' },
                            { label: 'PARTIAL', kind: 'warning', description: 'Data fetched for some accounts in the session but not all.' },
                            { label: 'PENDING', kind: 'info', description: 'Data fetch is still in progress.' },
                            { label: 'FAILED', kind: 'error', description: 'Data fetch failed for the session.' },
                        ]}
                    />
                    <StatusLegend
                        field="dataDeliveryStatus"
                        items={[
                            { label: 'FETCHED', kind: 'info', description: 'Raw data has been fetched from the FIP; report generation is still in progress.' },
                            { label: 'READY', kind: 'success', description: 'The analysed report has been generated and can be served via the data-serving APIs.' },
                            { label: 'READY_WITH_NO_TRANSACTIONS', kind: 'warning', description: 'Report generated successfully, but the account has no transactions in the requested period.' },
                            { label: 'DELIVERED', kind: 'neutral', description: 'The FIU has already fetched this report at least once.' },
                            { label: 'PENDING', kind: 'info', description: 'Delivery is yet to be completed.' },
                            { label: 'FAILED', kind: 'error', description: 'Report generation or delivery failed.' },
                        ]}
                    />
                    <StatusLegend
                        field="notificationType"
                        items={[
                            { label: 'FI_DATA', kind: 'info', description: 'Data fetched via the Account Aggregator channel.' },
                            { label: 'PDF_DATA', kind: 'info', description: 'Data extracted from an uploaded PDF bank statement.' },
                        ]}
                    />
                    <Typography fontSize={13} sx={{ color: '#4A5568', mt: 1.5 }}>
                        The <Box component="code" sx={inlineCodeStyle}>errStg</Box>, <Box component="code" sx={inlineCodeStyle}>errMsg</Box> and <Box component="code" sx={inlineCodeStyle}>attempt</Box> fields are populated when <Box component="code" sx={inlineCodeStyle}>status</Box> is <Box component="code" sx={inlineCodeStyle}>FAILED</Box>, capturing the upstream error stage, message and retry attempt respectively. <Box component="code" sx={inlineCodeStyle}>fip</Box> identifies the Financial Information Provider that served the data.
                    </Typography>
                </StepCard>

                <StepCard
                    stepNumber={5}
                    icon={<CloudDownloadOutlinedIcon fontSize="small" />}
                    title="Fetch the Data"
                    isLast
                >
                    <Typography fontSize={14} sx={{ mb: 1.5 }}>
                        Only after the data notification arrives with <Box component="code" sx={inlineCodeStyle}>dataDeliveryStatus</Box> = <Box component="code" sx={inlineCodeStyle}>READY</Box> should you call the data-serving APIs. Pick the report that matches your use case, in either JSON or Excel format:
                    </Typography>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
                        <Chip
                            size="small"
                            label="BSA report — Salaried / individual"
                            sx={{ backgroundColor: '#F5F3FF', color: '#4A3FA8', border: '1px solid #E9E4FB', fontWeight: 600 }}
                        />
                        <Chip
                            size="small"
                            label="SME report — MSME / self-employed"
                            sx={{ backgroundColor: '#F5F3FF', color: '#4A3FA8', border: '1px solid #E9E4FB', fontWeight: 600 }}
                        />
                        <Chip
                            size="small"
                            label="GST report — GST-based underwriting"
                            sx={{ backgroundColor: '#F5F3FF', color: '#4A3FA8', border: '1px solid #E9E4FB', fontWeight: 600 }}
                        />
                    </Stack>
                    <Typography fontSize={14}>
                        Browse the full list of endpoints in the{' '}
                        <Link
                            href="https://dhanaprayoga.fiu.finfactor.in/risk-gauge-internal/v1/swagger-ui/#/Data%20Serving%20Controller"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Data Serving Controller
                        </Link>
                        {' '}or in the API reference immediately below.
                    </Typography>
                </StepCard>
            </Box>
        </Box>
        <rapi-doc
            ref={rapidocRef}
            allow-try={true}
            allow-authentication={false}
            spec-url={""}
            show-info={false}
            show-header={false}
            sort-tags={false}
            sort-endpoints-by="none"
            render-style="view"
            theme="light"
            primary-color="#6C4FE0"
            bg-color="#FFFFFF"
            text-color="#2C3E50"
            header-color="#1A202C"
            nav-bg-color="#2D3748"
            nav-text-color="#E2E8F0"
            nav-hover-bg-color="#4A5568"
            nav-hover-text-color="#FFFFFF"
            nav-accent-color="#6C4FE0"
            schema-style="table"
            allow-server-selection={false}
            server-url={"https://dhanaprayoga.fiu.finfactor.in/"}
            regular-font='"Inter","Roboto","Helvetica","Arial",sans-serif'
            mono-font='"SF Mono","Monaco","Inconsolata","Roboto Mono",monospace'
            font-size="default"
            schema-description-expanded={true}
        ></rapi-doc>

        {/* Encryption Support Section */}
        <Box sx={{ paddingLeft: 4, paddingRight: 4, paddingBottom: 6 }}>
            <span id="encryption-support" />
            <Title title="Encryption Support" paddingTop={6} paddingBottom={1} />
            <Paragraph>
                Risk Gauge supports <strong>AES-256-GCM</strong> encryption for API request and response payloads.
                When enabled, all communication between your application and Risk Gauge is encrypted end-to-end,
                providing both confidentiality and tamper protection.
            </Paragraph>

            <Grid container spacing={3} sx={{ pt: 2, pb: 3, maxWidth: '80%' }}>
                <Grid size={4}>
                    <Paper elevation={0} sx={{ p: 2, border: '1px solid #E2E8F0', borderRadius: 2 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 14, pb: 0.5 }}>Authenticated Encryption</Typography>
                        <Typography sx={{ fontSize: 13, color: '#4A5568' }}>
                            AES-256-GCM (AEAD) ensures data cannot be read or tampered with in transit.
                        </Typography>
                    </Paper>
                </Grid>
                <Grid size={4}>
                    <Paper elevation={0} sx={{ p: 2, border: '1px solid #E2E8F0', borderRadius: 2 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 14, pb: 0.5 }}>Industry Standard</Typography>
                        <Typography sx={{ fontSize: 13, color: '#4A5568' }}>
                            Same encryption used in TLS 1.3, hardware-accelerated on modern CPUs.
                        </Typography>
                    </Paper>
                </Grid>
                <Grid size={4}>
                    <Paper elevation={0} sx={{ p: 2, border: '1px solid #E2E8F0', borderRadius: 2 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 14, pb: 0.5 }}>Simple Integration</Typography>
                        <Typography sx={{ fontSize: 13, color: '#4A5568' }}>
                            Straightforward envelope format with sample code in Java, Python, and Node.js.
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            <SubTitle>Envelope Format</SubTitle>
            <Paragraph>
                All encrypted payloads use a JSON envelope. The <code>payload</code> field contains
                Base64-encoded binary data, and <code>v</code> indicates the envelope version.
            </Paragraph>
            <CodeBlock title="Envelope Structure" language="json">
{`{
  "payload": "<Base64-encoded data>",
  "v": 1
}`}
            </CodeBlock>
            <Paragraph>
                The decoded payload is a byte sequence structured as:
            </Paragraph>
            <CodeBlock>
{`nonce (12 bytes) || ciphertext || authTag (16 bytes)`}
            </CodeBlock>
            <Box component="ul" sx={{ fontSize: 14, maxWidth: '75%', lineHeight: 1.8, pl: 2 }}>
                <li><strong>Nonce</strong> &mdash; 12 bytes (96-bit), randomly generated per request</li>
                <li><strong>Ciphertext</strong> &mdash; the encrypted JSON payload</li>
                <li><strong>Auth Tag</strong> &mdash; 16 bytes (128-bit), generated by GCM for integrity verification</li>
            </Box>

            <SubTitle>Key Generation</SubTitle>
            <Paragraph>
                Your encryption key must be exactly 256 bits (32 bytes). Generate one using OpenSSL:
            </Paragraph>
            <CodeBlock title="Shell" language="bash">
{`openssl rand -base64 32`}
            </CodeBlock>
            <Paragraph>
                Share the generated Base64 key with the FinFactor team during onboarding so it can be
                configured for your channel. Use separate keys for each environment (sandbox, production).
            </Paragraph>

            <SubTitle>Client Integration</SubTitle>
            <Paragraph>
                Encrypt your request body before sending it to any Risk Gauge API endpoint,
                and decrypt the response envelope you receive back.
            </Paragraph>
            <LanguageTabs />

            <SubTitle>Security Best Practices</SubTitle>
            <Box component="ul" sx={{ fontSize: 14, maxWidth: '75%', lineHeight: 2, pl: 2 }}>
                <li><strong>Secure key storage</strong> &mdash; Store your encryption key in a secrets manager (e.g., AWS Secrets Manager, Azure Key Vault, HashiCorp Vault). Never hardcode keys in source code.</li>
                <li><strong>Environment separation</strong> &mdash; Use different keys for sandbox and production environments.</li>
                <li><strong>Nonce uniqueness</strong> &mdash; Each encryption operation must use a fresh random nonce. All sample code above generates nonces automatically; do not reuse nonces.</li>
                <li><strong>Key rotation</strong> &mdash; Coordinate key rotation with the FinFactor team. Both old and new keys can remain active during a transition window, so no downtime is required.</li>
                <li><strong>Tamper detection</strong> &mdash; GCM mode automatically verifies data integrity. If a response fails decryption, treat it as potentially tampered and do not process it.</li>
            </Box>

            <SubTitle>Troubleshooting</SubTitle>
            <Box sx={{ maxWidth: '75%' }}>
                <Paper elevation={0} sx={{ p: 2, mb: 1.5, border: '1px solid #E2E8F0', borderRadius: 2 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, fontFamily: 'monospace', pb: 0.5 }}>
                        Failed to decrypt request body
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: '#4A5568' }}>
                        Verify your key matches the one configured for your channel. Ensure the envelope has both
                        <code> payload</code> and <code>v</code> fields, and that the payload is valid Base64.
                    </Typography>
                </Paper>
                <Paper elevation={0} sx={{ p: 2, mb: 1.5, border: '1px solid #E2E8F0', borderRadius: 2 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, fontFamily: 'monospace', pb: 0.5 }}>
                        AES-256 key must be 32 bytes
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: '#4A5568' }}>
                        Your key must decode to exactly 32 bytes. Run <code>echo -n "&lt;key&gt;" | base64 -d | wc -c</code> to
                        verify the decoded length.
                    </Typography>
                </Paper>
                <Paper elevation={0} sx={{ p: 2, mb: 1.5, border: '1px solid #E2E8F0', borderRadius: 2 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, fontFamily: 'monospace', pb: 0.5 }}>
                        AEADBadTagException / Authentication tag mismatch
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: '#4A5568' }}>
                        The ciphertext was modified in transit, the wrong key was used, or the nonce/ciphertext/tag
                        byte boundaries are incorrect. Double-check that the first 12 bytes are the nonce and the
                        last 16 bytes are the auth tag.
                    </Typography>
                </Paper>
            </Box>
        </Box>
    </>
});

export default RiskGaugePage;
